import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import type { CharacterPose, CharacterState, HomeActivity, HomeActivityDetail, HomeActivityDetailKind, HomeLife, HomeRoom, HomeSnapshot, PetPresence, PlotSummary, RhythmSky } from "@/shared/contracts";
import { queryKeys } from "@/shared/query/queryClient";
import { serverNow } from "@/shared/time/clock";
import { Button, Icon, Sheet, type IconName } from "@/shared/ui";
import { SceneControls } from "@/shared/ui/SceneControls";
import { preserveSceneState, SourceBack, SourceLink } from "@/shared/ui/sceneNavigation";
import cup from "./assets/living/ordinary-cup.webp";
import { GardenBed } from "@/features/farm/GardenScene";
import { produceVisual } from "@/features/farm/cropVisual";
// 菜园入口里的 GardenBed：几何在 garden-bed.css（GardenScene 自己引入），地块基础样式、摇摆 / 成熟动画与“减少动态效果”在 farm.css。
import "@/features/farm/farm.css";
import { mapFocusHref } from "@/features/world_map/mapFocus";
import { characterNote, PetFigure, sceneAsset } from "./PetFigure";
import { arrivalNotice } from "@/features/identity/arriving/arrivingModel";
import { Cat3DSlot } from "@/features/avatar3d/Cat3DSlot";
import { HomeEffects } from "./effects";
import { detailWord, FRAME, PLATES, plateForSky, bodyHeight, boxPct, forcedSky, isBehind, plateRectPct, SKY_PARAM, skyOf, tagCovers, toStage, type StagePoint } from "./homeStage";
import { useNightPlate } from "./useNightPlate";
import { env } from "@/shared/config/env";
import { PHOTO_FRAME } from "./HomeAlbum";
import { usePetStage } from "./usePetStage";
import { HomeMotionAdapter, type HomeMotionCapability } from "./HomeMotionAdapter";
import { sceneMotionBinding, sceneMotionMode, type HomeSceneMotionInput } from "./homeSceneMotion";

/**
 * 屋里的食盆（USER-V4 用户原型包 home-bowl，和已在用的 farm-basket、farm-seed-bag 同一来源）：只保留一只，不随状态出现消失。
 * 底图里没有食盆位（r7k 坐标表 null）：这只是独立物件，摆在中央地板右边、可走区外面一点（TA 走动不踩进盆里），原图像素换算到舞台。
 */
const BOWL_SRC = "/ui-assets/USER-V4/v1/home-bowl.webp";
const BOWLS: StagePoint[] = [toStage("inside", { x: 765, y: 1090 })];
/** 食盆画多宽（舞台像素）：和站在旁边的 TA 比，约半个身高 */
const BOWL_WIDTH = 130;

/** 点 TA 的反应（蹦一下、冒心）多久；与 home.css ps-pet-hop、ps-pet-heart 一致 */
const REACT_MS = 900;

/** 只看“减少动态效果”（TA 走不走、蹦不蹦；环境视频另外还看省流量） */
function subscribeReduced(onChange: () => void): () => void {
  const list = mediaQuery(REDUCED_MOTION);
  if (!list) return () => {};
  if (typeof list.addEventListener === "function") list.addEventListener("change", onChange);
  else list.addListener(onChange);
  return () => {
    if (typeof list.removeEventListener === "function") list.removeEventListener("change", onChange);
    else list.removeListener(onChange);
  };
}
function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReduced, () => mediaQuery(REDUCED_MOTION)?.matches ?? false, () => false);
}
function subscribeSceneVisibility(onChange: () => void) {
  document.addEventListener("visibilitychange", onChange);
  return () => document.removeEventListener("visibilitychange", onChange);
}

/**
 * TA 站在这一点时宠物框的位置与大小（场景盒子百分比）：框高＝站姿主体高（按远近），脚底对准框底边中点；
 * 框宽给到主体高的 1.4 倍（横着的睡姿、肖像都放得下）。
 */
function petBox(room: HomeRoom, at: StagePoint): CSSProperties {
  const h = bodyHeight(room, at.y);
  const heightPct = (h / (FRAME.bottom - FRAME.top)) * 100;
  const widthPct = ((h * 1.4) / FRAME.width) * 100;
  const { left, top } = boxPct(at);
  return { left: `${(left - widthPct / 2).toFixed(2)}%`, top: `${(top - heightPct).toFixed(2)}%`, width: `${widthPct.toFixed(2)}%`, height: `${heightPct.toFixed(2)}%` };
}

/** One isotropic camera for the existing logical frame and the complete visible plate.
 * Width is preserved unless the image's available rows cannot cover the height.
 * A short/wide viewport scrolls through the original depth instead of losing entrances.
 */
export function homeViewportFit(room: HomeRoom, width: number, availableHeight: number) {
  if (!Number.isFinite(width) || !Number.isFinite(availableHeight) || width <= 0 || availableHeight <= 0) return null;
  const plate = PLATES[room];
  const frameHeight = FRAME.bottom - FRAME.top;
  const visibleRows = (plate.height - plate.cropTop) * FRAME.width / plate.width;
  const height = Math.max(availableHeight, width * frameHeight / FRAME.width);
  const scale = Math.max(width / FRAME.width, height / visibleRows);
  const stageWidth = FRAME.width * scale;
  return { width, height, scale, left: (width - stageWidth) / 2, stageWidth, stageHeight: frameHeight * scale, visiblePlateHeight: visibleRows * scale };
}

function useHomeViewportFit(room: HomeRoom, viewport: RefObject<HTMLDivElement | null>) {
  const [fit, setFit] = useState<ReturnType<typeof homeViewportFit>>(null);
  useLayoutEffect(() => {
    const area = viewport.current;
    if (!area) return;
    const visual = window.visualViewport;
    const measure = () => {
      const available = (visual?.height ?? window.innerHeight) - Math.max(0, area.getBoundingClientRect().top - (visual?.offsetTop ?? 0));
      const next = homeViewportFit(room, area.clientWidth, available);
      setFit(previous => previous?.width === next?.width && previous?.height === next?.height && previous?.scale === next?.scale ? previous : next);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(area);
    window.addEventListener("resize", measure);
    visual?.addEventListener("resize", measure);
    visual?.addEventListener("scroll", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      visual?.removeEventListener("resize", measure);
      visual?.removeEventListener("scroll", measure);
    };
  }, [room, viewport]);
  return fit;
}

/* PLATES and homeStage remain the sole original-image coordinate contract.
 * The stage is sized in physical pixels without a CSS transform, so HomeEffects
 * measured rectangles are not scaled a second time. Background and all scene
 * objects share its original FRAME ratio; only the viewport clips its sides.
 * No current PLATES.motion exists; unmatched old motion assets remain disabled.
 */
/** 环境微动视频要求的编码（avcC：High profile、level 3.2）；浏览器说放不了就只留静态底图。 */
const SCENE_MOTION_TYPE = 'video/mp4; codecs="avc1.640020"';
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const REDUCED_DATA = "(prefers-reduced-data: reduce)";

type NetworkInformation = EventTarget & { saveData?: boolean };
const networkInformation = (): NetworkInformation | undefined => (navigator as Navigator & { connection?: NetworkInformation }).connection;
const mediaQuery = (query: string): MediaQueryList | null => (typeof window.matchMedia === "function" ? window.matchMedia(query) : null);

/** 这会儿要不要环境微动：打开了“减少动态效果”、省流量（Save-Data / prefers-reduced-data）都不要。 */
function motionWanted(): boolean {
  return !mediaQuery(REDUCED_MOTION)?.matches && !mediaQuery(REDUCED_DATA)?.matches && networkInformation()?.saveData !== true;
}

/** 上面几项中途变了（比如打开了“减少动态效果”）马上跟着变；老浏览器的 MediaQueryList 没有 addEventListener，用 addListener。 */
function subscribeMotionWanted(onChange: () => void): () => void {
  const lists = [mediaQuery(REDUCED_MOTION), mediaQuery(REDUCED_DATA)].filter((list): list is MediaQueryList => list !== null);
  const network = networkInformation();
  for (const list of lists) {
    if (typeof list.addEventListener === "function") list.addEventListener("change", onChange);
    else list.addListener(onChange);
  }
  network?.addEventListener?.("change", onChange);
  return () => {
    for (const list of lists) {
      if (typeof list.removeEventListener === "function") list.removeEventListener("change", onChange);
      else list.removeListener(onChange);
    }
    network?.removeEventListener?.("change", onChange);
  };
}

/** 底图画出来以后，再静这么久才挂环境微动（见 whenBasePainted）。 */
const MOTION_SETTLE_MS = 1000;

/**
 * 底图画出来、再静一会儿才回调（HomeScene 据此挂环境微动）：
 * - load 只说明下载完了；等 decode() 完成才算能画（有的浏览器对大图异步解码，没解完先不画，视频反而会先出来）；
 * - decode() 完成时大图往往还在光栅化、没上屏（无头 Chrome 实测），这时起视频会和它抢，底图上屏跟着晚；
 *   所以再等下一帧、静 1 秒：底图先安安静静地出来，过一会儿再动起来，视频的下载也不和首屏的其他东西抢；
 * - 等帧用 requestAnimationFrame：后台打开的标签页里它不走，也就不挂视频、不下载；
 * - 回调时底图已经换了（这期间换了房间）就不报：晚到的旧回调不会盖掉新房间的。decode() 失败也照样往下走。
 */
function whenBasePainted(img: HTMLImageElement, src: string, done: (src: string) => void) {
  const settle = () =>
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (img.getAttribute("src") === src) done(src);
      }, MOTION_SETTLE_MS);
    });
  (typeof img.decode === "function" ? img.decode() : Promise.resolve()).then(settle, settle);
}

/** 环境微动能不能放：要（见上）并且这台设备放得了 H.264（这一项不会中途变，挂上时问一次）。 */
function useSceneMotionAllowed(): boolean {
  const wanted = useSyncExternalStore(subscribeMotionWanted, motionWanted, () => false);
  const [playable] = useState(() => document.createElement("video").canPlayType(SCENE_MOTION_TYPE) !== "");
  return wanted && playable;
}

/**
 * 环境微动视频：按 cover 居中放进 390:610 的盒子；只挂画面和盒子里露出的那一段底图逐行对齐的（PLATES[room].motion，新底图现在都没有）。
 * 纯装饰：对读屏隐藏、不接收点按（样式在 home.css .ps-living-motion）。
 * - 底图先出：HomeScene 等底图画出来、再静 1 秒（whenBasePainted）才挂上它，挂上以后才给 src；开始播了才淡入，之前透明，看到的一直是底图。
 * - 只用 autoplay muted loop playsInline。标签页隐藏、场景滚出视野就暂停，回来接着播。
 * - 播放出错、恢复播放被拒（比如省电模式）：卸掉视频，只剩静态底图。
 * - 卸下（换房间、中途打开“减少动态效果”）时先停、清掉 src 再 load()，把下载也断掉：同一时刻只有一条视频在下、在播。
 */
function SceneMotion({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let live = true;
    let inView = true;
    // 是我们（隐藏 / 滚出视野）停的：回来时由我们接着放；从没开始播的（比如自动播放被拦）不去硬放
    let held = false;
    const stop = () => {
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
    const fail = () => {
      if (!live) return;
      live = false;
      stop();
      setFailed(true);
    };
    const sync = () => {
      if (!live) return;
      if (document.visibilityState === "hidden" || !inView) {
        held = true;
        video.pause();
      } else if (held) {
        held = false;
        // 刚放又被暂停打断（AbortError）不算失败；别的拒绝（不许自动播放等）才卸掉
        video.play()?.catch((error: unknown) => {
          if ((error as { name?: unknown } | null)?.name !== "AbortError") fail();
        });
      }
    };
    const observer =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver((entries) => {
            inView = entries[entries.length - 1]?.isIntersecting ?? inView;
            sync();
          })
        : null;
    video.addEventListener("error", fail);
    document.addEventListener("visibilitychange", sync);
    observer?.observe(video);
    // React 只设 muted 这个属性值、不写 muted 特性；有的浏览器按特性判断能不能静音自动播放
    video.defaultMuted = true;
    video.muted = true;
    video.src = src;
    sync();
    return () => {
      live = false;
      video.removeEventListener("error", fail);
      document.removeEventListener("visibilitychange", sync);
      observer?.disconnect();
      stop();
    };
  }, [src]);
  if (failed) return null;
  return (
    <video
      ref={ref}
      className={`ps-living-motion${playing ? " is-playing" : ""}`}
      autoPlay
      muted
      loop
      playsInline
      aria-hidden="true"
      data-testid="home-scene-motion"
      onPlaying={() => setPlaying(true)}
    />
  );
}

/** Only the isolated internal test harness supplies a character. No default pet identity. */
export const LivingCharacterContext = createContext<{
  petId: string;
  rest: string;
  sit: string;
  bag: string;
} | null>(null);

/**
 * 院子里菜园入口露出的那一块地：和菜园页同一个 GardenBed——开垦土块，作物的入土点对准垄沟，前景土层挡住入土的那一截，
 * 成熟时的光由代码加（样式在 farm 的 garden-bed.css / farm.css）。外面这层仍是原来 92px 高的框，入口按钮的位置和大小不变。
 */
export function PlotArt({ plot }: { plot: PlotSummary }) {
  return (
    <span className="ps-living-plot-art" aria-hidden="true">
      <GardenBed cropKey={plot.crop_key} stage={plot.stage} />
    </span>
  );
}

/**
 * 这一页之前有没有站内的一页：与顶栏返回同一个判断（直接打开时，初始条目的 key 是 "default"）。
 * 浏览器里再核一次 React Router 记在 history.state 里的序号 idx：直接打开后换过房间（replace 会换 key）仍算没有来路，
 * 不然后退会退出站点。内存路由（测试）没有这个序号，只按 key 判断。
 */
/** The current-tab predecessor is checked by SourceBack; a non-default key is not proof. */
export function HomeBackLink({ inline = false, petId = null }: { inline?: boolean; petId?: string | null }) {
  return <SourceBack fallback={mapFocusHref(petId)} parentPath="/map" className={`ps-home-back${inline ? " is-inline" : ""}`} aria-label="回到地图"><Icon name="pin" size={16} />地图</SourceBack>;
}

/** 有事实表明 TA 在外面的几种状态；unknown / not_activated 不算，不替 TA 写“出门啦”。 */
const AWAY_PRESENCE: ReadonlySet<PetPresence> = new Set<PetPresence>(["in_transit", "at_destination", "visiting", "returning"]);

/* ---------- L2-0：TA 此刻在家里哪一间、在做什么（后端 home_life 投影；前端只读，合同 L2-0-HOME-PRESENCE-RHYTHM 第 6 节） ---------- */

/**
 * 当前这只宠物的 life：就在同一份快照的 `pets[]` 里（后端 `pet_briefs`，与 `household.pets` 同一份），不另找来源。
 * 旧后端、演示快照没有 → null，场景照原来的做法（默认庭院、按 presence 画、中性姿态）。
 * 在家却没有位置（后端不这样给）也按没有处理：说不清 TA 在哪一间，就不替 TA 挑一间。
 */
function homeLifeOf(snapshot: HomeSnapshot): HomeLife | null {
  const petId = snapshot.pet.pet_id;
  const life = (snapshot.pets ?? snapshot.household?.pets)?.find((pet) => pet.pet_id === petId)?.life ?? null;
  return life && (life.activity.kind === "away" || life.position) ? life : null;
}

/** TA 此刻在不在外面：有 life 只看 activity.kind；没有 life 按 presence（和场景里画不画 TA、放不放出门便签同一个判断） */
export function petIsAway(snapshot: HomeSnapshot): boolean {
  const life = homeLifeOf(snapshot);
  return life ? life.activity.kind === "away" : AWAY_PRESENCE.has(snapshot.presence);
}

/**
 * 出门便签上的字（HomePage 按地图同一份状态、同一套说法算好传进来，只说接口给的事实）：
 * title 一句 TA 在哪、在做什么（如“我在回家的路上”），detail 一到两行补充、用换行分开（如“到家还要 13 分钟”），没有就不写。
 */
export interface AwayNote {
  title: string;
  detail: string | null;
}

/** Actual transparent material; its failure never removes the truthful DOM label or link. */
function CottageMaterial({ src, className, width, height, children }: { src: string; className: string; width: number; height: number; children: ReactNode }) {
  const [failed, setFailed] = useState(false);
  return <span className={`ps-cottage-material ${className}${failed ? " is-art-failed" : ""}`}>
    {!failed ? <img className="ps-cottage-material__art" src={src} width={width} height={height} alt="" decoding="async" draggable={false} onError={() => setFailed(true)} /> : null}
    {children}
  </span>;
}

/** Decorations stay outside the original walk core, and never participate in input or physics. */
function CottageDecoration({ kind, src, rect, width, height }: { kind: "flowerpot" | "crate"; src: string; rect: { x: number; y: number; width: number; height: number }; width: number; height: number }) {
  const [failed, setFailed] = useState(false);
  const r = plateRectPct("courtyard", rect);
  if (failed) return null;
  return <span className="ps-cottage-decor" aria-hidden="true" data-testid={`home-cottage-${kind}`} style={{ left: `${r.left}%`, top: `${r.top}%`, width: `${r.width}%`, height: `${r.height}%` }}>
    <img src={src} width={width} height={height} alt="" decoding="async" draggable={false} onError={() => setFailed(true)} />
  </span>;
}

const ROOM_WORD: Record<HomeRoom, string> = { inside: "屋里", courtyard: "院子里" };

/**
 * R16（2531 ADVISORY-R16 v0.2）：醒着、并且此刻真在进行的那一段是“翻这趟旅行的照片”（detail_kind 为 phone:album）。
 * 后端只在这一段真的开始了才给 detail（开关关着、空档、到家那 60 秒都为空），所以看到它就是真在发生；前端不推算、不按本机时钟改。
 * detail_kind 在合同里是字符串不是枚举：只认这一个，认不出的一律照旧说“在家”。其余在家活动各要单独定文案、单独放行，这里不加。
 */
const ALBUM_DETAIL = "phone:album";
const ALBUM_WORD = "在翻这趟旅行的照片";

function albumDetail(activity: HomeActivity | null): HomeActivityDetail | null {
  return activity?.kind === "awake" && activity.detail?.detail_kind === ALBUM_DETAIL ? activity.detail : null;
}

/**
 * TA 的无障碍名称、点 TA 冒出的气泡说的活动：只说 life 说得清的——睡着、刚到家、翻这趟的照片，
 * 以及家园改版（2026-10-02 用户定“前端一定要优化”、状态变化要看得见）后醒着时画面上看得出的那几种在做的事（homeStage DETAIL_WORD）；
 * 看窗外按后端天色说（homeStage detailWord：白天“在窗边往外看”，R27 第 1 条）；
 * 其余醒着、不知道都只说“在家”。
 */
function activityWord(activity: HomeActivity | null, sky: RhythmSky | null = null): string | null {
  if (activity?.kind === "sleeping") return "在睡觉";
  if (activity?.kind === "arriving") return "刚到家";
  if (albumDetail(activity)) return ALBUM_WORD;
  if (activity?.kind === "awake" && activity.detail) return detailWord(activity.detail.kind, sky) ?? null;
  return null;
}

/** TA 头顶那一句：TA 此刻在哪、在做什么（只读 life 与 presence；不知道的不编）。画面里没有 TA 时这一句只给读屏，见 HomeScene */
function statusLine(name: string, life: HomeLife | null, away: boolean, home: boolean): string {
  if (away) return `${name}出门了`;
  if (!home) return `${name}`;
  const room = life?.position?.room;
  const where = room ? ROOM_WORD[room] : null;
  const kind = life?.activity.kind;
  const doing = activityWord(life?.activity ?? null, life?.rhythm?.sky ?? null);
  if (kind === "sleeping") return where ? `${name}在${where}睡觉` : `${name}在睡觉`;
  if (kind === "arriving") return `${name}刚到家`;
  // 在做的事：说法里已经带了地方（“在院子里溜达”“在屋里歇着”“在窗边往外看”）就照说，没带就补上在哪一间（“在屋里吃饭”）
  if (doing) return `${name}${/屋里|院子|窗台|窗边/.test(doing) || !where ? doing : `在${where}${doing.replace(/^在/, "")}`}`;
  if (kind === "awake") return where ? `${name}醒着，在${where}` : `${name}醒着`;
  return `${name}在家`;
}

/**
 * “陪 TA 待一会儿”面板副标题里的活动：翻这趟照片时用服务端的理由句（按已提交的事实套模板，和后台同一个来源；模板不带主语，页面照旧在前面加“TA ”）；
 * 理由为空或只有空格时退回“在翻这趟旅行的照片”。其余同气泡。B/C 改理由模板（home_choice.DETAIL）时要知会 UI：前端用例拦不住模板改字，要重量 320、重核字样。
 */
function panelActivity(activity: HomeActivity | null, sky: RhythmSky | null = null): string | null {
  const reason = albumDetail(activity)?.reason?.trim();
  return reason ? reason : activityWord(activity, sky);
}

/**
 * “在做什么 · 形象提示”：两句各作为一段不拆（行内块，比栏宽才在块里折行），放不下就在“ · ”处换行，“·”留在上一行末尾。
 * R16 实测：“在翻这趟旅行的照片”拼上“ · 正在准备 TA 的形象”一类提示后，拼成一串在 320、375 的气泡里都折出孤字，面板在 320 从“正在｜准备”中间断开。
 * 没有形象提示时就是原来那一串字；有时 textContent 仍是“在家 · 提示”原样，按整串断言的用例照旧。
 */
function StatusLine({ text, note }: { text: string; note: string | null }) {
  if (!note) return <>{text}</>;
  return (
    <>
      <span className="ps-home-status-seg">{text} ·</span> <span className="ps-home-status-seg">{note}</span>
    </>
  );
}

/** 到了边界再等这么一会儿才重取：服务器那边正好跨过去，回来的就是新状态。 */
const BOUNDARY_GRACE_MS = 1000;
/** setTimeout 能排的最长时间（约 24.8 天）；更远的边界按它排，到时重取一次。 */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * 不按本机时钟推作息（第 6 节第 4 条）：到后端给的下一个边界就让快照重取一次，TA 睡没睡以回来的快照为准，页面自己不改状态。
 * 边界是 `rhythm.next_change_at`（下一次入睡或起床）；TA 刚到家时还有这 60 秒的结束（`activity.until`），头上的“刚到家”到点就收；
 * 正在说“在翻这趟旅行的照片”时还有这一段的结束（`activity.detail.until`），到点就收（R16：只在这句显示时才加，不显示就不多发请求）。
 * 时间按服务器校准过的“现在”（serverNow）算。同一个边界只排一次：回来的还是这个边界（服务器还没跨过去）时 due 没变、不再排，
 * 交给页面原有的定时刷新（HomePage 每分钟一次）。没有 QueryClient（只渲染场景的用例）时什么也不做。
 */
function useRefetchAtBoundary(life: HomeLife | null) {
  const client = useContext(QueryClientContext);
  const arrivingUntil = life?.activity.kind === "arriving" ? life.activity.until : null;
  const albumUntil = albumDetail(life?.activity ?? null)?.until ?? null;
  const times = [life?.rhythm?.next_change_at ?? null, arrivingUntil, albumUntil].map((at) => (at ? Date.parse(at) : NaN)).filter((ms) => !Number.isNaN(ms));
  const due = times.length > 0 ? Math.min(...times) : null;
  useEffect(() => {
    if (!client || due === null) return;
    const timer = setTimeout(() => {
      void client.invalidateQueries({ queryKey: queryKeys.home });
    }, Math.min(Math.max(0, due - serverNow()) + BOUNDARY_GRACE_MS, MAX_TIMER_MS));
    return () => clearTimeout(timer);
  }, [client, due]);
}

/* ---------- 第一次进来：可点的东西依次轻闪一次；按宠物记在本机，之后不再打扰 ---------- */
const HINT_START_MS = 400;
const HINT_STEP_MS = 480;
/** 与 home.css 里 `ps-home-hint` 的时长保持一致。 */
const HINT_GLOW_MS = 1100;
const introKey = (petId: string) => `petsoul:home-intro:${petId}`;

function introSeen(petId: string): boolean {
  try {
    return window.localStorage.getItem(introKey(petId)) === "1";
  } catch {
    return false; // 读不到就当第一次
  }
}

function rememberIntro(petId: string) {
  try {
    window.localStorage.setItem(introKey(petId), "1");
  } catch {
    // 记不住只会让下次进来再提示一次，不影响使用
  }
}

/** 暂停时（到家时刻还开着）先不提示、也不记；同一次停留里只试一次，换房间不会重播。 */
function useFirstVisitHint(petId: string, paused: boolean, count: number): boolean {
  const [hintFor, setHintFor] = useState<string | null>(null);
  const tried = useRef(new Set<string>());
  useEffect(() => {
    if (paused || count === 0 || tried.current.has(petId)) return;
    tried.current.add(petId);
    if (introSeen(petId)) return;
    rememberIntro(petId);
    setHintFor(petId);
  }, [petId, paused, count]);
  useEffect(() => {
    if (!hintFor) return;
    const timer = setTimeout(() => setHintFor(null), HINT_START_MS + Math.max(0, count - 1) * HINT_STEP_MS + HINT_GLOW_MS + 200);
    return () => clearTimeout(timer);
  }, [hintFor, count]);
  return hintFor === petId;
}

/* ---------- 点到才冒出来的小气泡：名字 + 能做的事 ---------- */
/** 气泡冒在物件的哪一侧（放不下时翻到另一侧）。 */
type BubbleSide = "top" | "bottom" | "left" | "right";
type ThingKey = "door" | "cabinet" | "pet" | "memento" | "garden";
/**
 * 左侧“手机”入口（原来叫信箱）：进 TA 的手机首屏（P2，用户 10-03「通讯器更应该像是一个手机」）。
 * 首屏里每个聊天（我和 TA、家庭群）各自挂未读几条，点哪个进哪个、在那里看到最新一条就清——
 * 原来“只有家庭来信有未读时直接开家庭来信”的分流（R26 第 1 条）不再需要。
 */
export const PHONE_HREF = "/communicator?channel=phone";

type RailKey = "rail-photo" | "rail-play" | "rail-mail" | "rail-memories";
type OpenKey = ThingKey | RailKey;
const BUBBLE_GAP = 8;
const BUBBLE_EDGE = 8;

/**
 * 把气泡放在物件旁边，并收进场景里（场景会裁掉溢出的部分）；放不下就翻到另一侧。
 * 和左侧小图标同一高度时不压住它们（窄屏上收藏柜的气泡会往左伸到小图标上）。
 */
function placeBubble(scene: DOMRect, anchor: DOMRect, bubble: DOMRect, place: BubbleSide, avoid: DOMRect | null) {
  const { width: w, height: h } = bubble;
  let left: number;
  let top: number;
  if (place === "top" || place === "bottom") {
    left = anchor.left + anchor.width / 2 - w / 2;
    top = place === "top" ? anchor.top - BUBBLE_GAP - h : anchor.bottom + BUBBLE_GAP;
    if (place === "top" && top < scene.top + BUBBLE_EDGE) top = anchor.bottom + BUBBLE_GAP;
    if (place === "bottom" && top + h > scene.bottom - BUBBLE_EDGE) top = anchor.top - BUBBLE_GAP - h;
  } else {
    top = anchor.top + anchor.height / 2 - h / 2;
    left = place === "left" ? anchor.left - BUBBLE_GAP - w : anchor.right + BUBBLE_GAP;
    if (place === "left" && left < scene.left + BUBBLE_EDGE) left = anchor.right + BUBBLE_GAP;
    if (place === "right" && left + w > scene.right - BUBBLE_EDGE) left = anchor.left - BUBBLE_GAP - w;
  }
  top = Math.max(scene.top + BUBBLE_EDGE, Math.min(top, scene.bottom - BUBBLE_EDGE - h));
  const besideRail = avoid && top < avoid.bottom && top + h > avoid.top;
  const minLeft = besideRail ? avoid.right + BUBBLE_GAP : scene.left + BUBBLE_EDGE;
  left = Math.max(minLeft, Math.min(left, scene.right - BUBBLE_EDGE - w));
  return { left: Math.round(left - scene.left), top: Math.round(top - scene.top) };
}

function Bubble({
  id,
  name,
  detail,
  note = null,
  place,
  anchorKey,
  anchorOf,
  scene,
  viewport,
  rail,
  cameraKey,
  children,
}: {
  id: string;
  name: string;
  detail?: string | null;
  /** 跟在 detail 后面的形象提示（只有 TA 的气泡有）：和 detail 各作为一段不拆，见 StatusLine */
  note?: string | null;
  place: BubbleSide;
  anchorKey: string;
  anchorOf: (key: string) => HTMLElement | null;
  scene: RefObject<HTMLElement | null>;
  viewport?: RefObject<HTMLDivElement | null>;
  rail: RefObject<HTMLElement | null>;
  /** 场景裁切（camera）各值拼成的键：窗口只变宽窄时舞台不变大小、只平移，ResizeObserver 不报，靠它在舞台挪完以后再量一次（UI2-REVIEW110-01，6c2b 查出） */
  cameraKey: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const bubble = ref.current;
      const anchor = anchorOf(anchorKey);
      const area = scene.current;
      if (!bubble || !anchor || !area) return;
      const stageRect = area.getBoundingClientRect();
      const visibleRect = viewport?.current?.getBoundingClientRect() ?? stageRect;
      const placed = placeBubble(visibleRect, anchor.getBoundingClientRect(), bubble.getBoundingClientRect(), place, rail.current?.getBoundingClientRect() ?? null);
      const next = { left: placed.left + visibleRect.left - stageRect.left, top: placed.top + visibleRect.top - stageRect.top };
      setAt((prev) => (prev && prev.left === next.left && prev.top === next.top ? prev : next));
    };
    measure();
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (scene.current) observer?.observe(scene.current);
    if (viewport?.current) observer?.observe(viewport.current);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, [anchorKey, anchorOf, scene, viewport, rail, place, name, detail, note, cameraKey]);
  return (
    <div ref={ref} id={id} role="group" aria-label={name} className="ps-home-bubble" style={at ? { left: at.left, top: at.top } : undefined}>
      <strong className="ps-home-bubble__name">{name}</strong>
      {detail ? (
        <span className="ps-home-bubble__detail">
          <StatusLine text={detail} note={note} />
        </span>
      ) : null}
      <div className="ps-home-bubble__actions">{children}</div>
    </div>
  );
}

/**
 * 小窝里 TA 用哪张姿态图（I 10-03 最高优先：用户要“上传宠物就能生成所有的姿态”“设计的都是要打开的……固定打开”，P 已真实生成全套）：
 * - 睡着：睡姿（缺睡姿照旧由 PetFigure 画本人肖像加“在睡觉”）；
 * - 点 TA 那 0.9 秒：被摸（petted）；
 * - 在吃饭（醒着、detail 是 eat）：吃饭（eating）；
 * - 在走（走走停停、转场走过去的那一段）或在院子里溜达：走动（walking）；
 * - 醒着歇着（rest）、看电视（watch_tv）：晒太阳那张（sunbathing，睁眼侧躺）——10-03 晚起（c84a 1046Z 点名站姿却写“歇着”，6796 核、I 认）。
 *   文字照旧“歇着”，不说晒太阳；看窗外、玩手机仍站着。原来不接是因为它的透明边两判都没过，2531 pre1530 实走记“晒太阳无白边”，FRINGE 修复已生效；
 * - 其余：中性站姿。
 * 想要的那张这一套里没有（没生成、没过检查）就回落中性站姿。横图按同一套的中性站姿身体高度定比例、按实测脚点落地（PetFigure），和睡姿同一条路。
 * 只看这一只宠物自己的形象（state 是当前宠物的），换宠物就跟着换，不串号。
 */
export function scenePose(
  state: CharacterState | null,
  { kind, detail, walking, petted }: { kind: HomeActivity["kind"] | null | undefined; detail: HomeActivityDetailKind | null | undefined; walking: boolean; petted: boolean },
): CharacterPose {
  if (kind === "sleeping") return "sleeping";
  const awake = kind === "awake";
  const lounging = awake && (detail === "rest" || detail === "watch_tv");
  const want: CharacterPose = petted ? "petted" : awake && detail === "eat" ? "eating" : walking || (awake && detail === "stroll_courtyard") ? "walking" : lounging ? "sunbathing" : "neutral_full";
  return want !== "neutral_full" && sceneAsset(state, want) ? want : "neutral_full";
}

/**
 * 侧边小图标：本身就是入口（有地址、有无障碍名称）。图标下面一直写着短名字（I 10-03 清单第 2 件、R27 第 9 条：
 * 原来平时不写字，玩家不知道点了去哪），所以点一下就直接去——触屏不再先冒出写名字的小气泡。
 * 读屏念的是无障碍名称（完整说法、几条未读），短名字只给眼睛看。
 */
function RailEntry({
  railKey,
  to,
  short,
  label,
  icon,
  art,
  artSet,
  dot = false,
  register,
}: {
  railKey: RailKey;
  to: string;
  /** 图标下面那两三个字 */
  short: string;
  label: string;
  icon: IconName;
  /** 用户提供的入口插画；失败后本次挂载只保留原语义图标，不循环重载。 */
  art: string;
  artSet?: string;
  dot?: boolean;
  register: (key: OpenKey) => (el: HTMLElement | null) => void;
}) {
  const [artFailed, setArtFailed] = useState(false);
  return (
    <div className="ps-home-rail__item">
      <SourceLink ref={register(railKey)} to={to} className="ps-home-rail__btn" aria-label={label}>
        <span className="ps-home-rail__art" aria-hidden="true">
          {artFailed ? <Icon name={icon} size={18} /> : (
            <img src={art} srcSet={artSet} sizes="30px" alt="" aria-hidden="true" width={30} height={30} decoding="async" draggable={false} onError={() => setArtFailed(true)} />
          )}
        </span>
        <span className="ps-home-rail__label">{short}</span>
        {/* 有新消息只挂一个小红点，不写数字；几条写在无障碍名称里 */}
        {dot ? <span className="ps-home-rail__dot" aria-hidden="true" data-testid="home-rail-dot" data-motion="enter" /> : null}
      </SourceLink>
    </div>
  );
}

/** A new pet/set/action asset owns fresh timers, effects and open controls. */
export function HomeScene(props: Parameters<typeof HomeSceneBody>[0]) {
  const supplied = props.worldCharacter;
  const own = supplied?.pet_id === props.snapshot.pet.pet_id && (!supplied.active || supplied.active.pet_id === props.snapshot.pet.pet_id) ? supplied : null;
  const set = own?.active;
  const identity = `${props.snapshot.pet.pet_id}:${set?.character_set_id ?? "-"}:${set?.revision ?? "-"}:${set?.reference_version ?? "-"}:${set?.style_version ?? "-"}:${set?.assets.map(a => `${a.asset_id}:${a.content_sha256}:${a.url}`).join("|") ?? "-"}:${props.sceneMotion?.identityGeneration ?? "-"}:${props.sceneMotion?.bodyRevision ?? "-"}`;
  return <HomeSceneBody key={identity} {...props} worldCharacter={own} />;
}

function HomeSceneBody({
  snapshot,
  worldCharacter = null,
  petActions = null,
  tablePhoto = null,
  introPaused = false,
  awayNote = null,
  sceneMotion = null,
  notice = null,
}: {
  snapshot: HomeSnapshot;
  worldCharacter?: CharacterState | null;
  petActions?: ReactNode;
  /** 屋里桌上的小相框（HomePage 传 HomeAlbumFrame：TA 带回来的照片，走真实相册读口；没有照片就什么都不画） */
  tablePhoto?: ReactNode;
  /** 到家时刻这类盖在上面的卡片还开着时为 true：第一次的轻闪提示等它关掉再放。 */
  introPaused?: boolean;
  /** 出门便签上的字（HomePage 算好传进来）；没有时便签照旧写“我出门啦 / 看看我走到哪啦” */
  awayNote?: AwayNote | null;
  /** Explicit I-owned reader result; absent keeps current-pet static fallback. */
  sceneMotion?: HomeSceneMotionInput | null;
  /** 场景底部的告示栏（HomePage 传 HomeBulletin：地址落定中、还在路上、家在哪、一次性告知；UI2-HOME-SMALL-01、UI2-REVIEW110-01）：挂在视口上、不随镜头平移，压在 TA 走不到的那一截 */
  notice?: ReactNode;
}) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // L2-0：TA 在哪一间、在做什么只读 life（事实）；镜头看哪一间是另一回事，切房间只改镜头。
  const life = homeLifeOf(snapshot);
  const kind = life?.activity.kind ?? null;
  const factRoom = life?.position?.room ?? null;
  useRefetchAtBoundary(life);
  // 镜头：地址里点过的那一间（?room=，换房间写在这里）；没点过就对着 TA 所在的那一间；没有 life（或 TA 出门了）照旧默认庭院。
  const asked = params.get("room");
  const room: HomeRoom = asked === "inside" || asked === "courtyard" ? asked : factRoom ?? "courtyard";
  const inside = room === "inside";
  // 天色只认后端作息；演示数据（fixture）里可以用 ?sky= 看别的天色（homeStage.forcedSky，只换画面，TA 怎么说照旧读作息）
  const sky = forcedSky({ dataMode: env.dataMode, dataOrigin: snapshot.data_origin, value: params.get(SKY_PARAM) }) ?? skyOf(life);
  // 夜图什么时候换上（开着页面昼夜交替时先下好整套再一次换、同一间淡入；夜图下不来留白天图）：useNightPlate
  const nightPlate = useNightPlate(room, sky === "night");
  const plate = plateForSky(room, nightPlate.night ? "night" : "day");
  const base = plate.src;
  // 淡入时垫在下面的旧图（同一间、几何完全一样，只换图）
  const fadeFrom = nightPlate.fadeFrom ? plateForSky(room, nightPlate.fadeFrom.night ? "night" : "day") : null;
  const motionAllowed = useSceneMotionAllowed();
  // 这一间的底图画出来、静够了没有（whenBasePainted 报上来的地址）：到了才挂环境微动（先出底图）
  const [baseLoaded, setBaseLoaded] = useState<string | null>(null);
  // 在不在家：有 life 只看 activity.kind（away 就不画、不留分身）；没有 life 照旧按 presence。
  const home = life ? kind !== "away" : snapshot.presence === "at_home";
  const away = life ? kind === "away" : AWAY_PRESENCE.has(snapshot.presence);
  // TA 在不在镜头这一间：有位置只看 position.room；另一间时这里不画 TA。
  // 用户 10-03：在屋里、在院子里不需要纸条提醒 TA 在哪一间（原来那张“TA 在屋里 / 进屋看看”撤了）；TA 在哪由页签切过去看，读屏照旧有顶栏里那一句状态。
  const here = home && (factRoom === null || factRoom === room);
  // TA 在家、但在另一间：那一间的页签上挂个小图钉（不贴纸条）
  // 活动怎么说只读 life.activity（R16：翻这趟照片看 detail）；用哪张姿态图见下面 scenePose（要等走没走、点没点）。
  const activity = life?.activity ?? null;
  const doing = activityWord(activity, life?.rhythm?.sky ?? null);
  const character = useContext(LivingCharacterContext);
  const sprite =
    snapshot.data_origin === "fixture" &&
    character?.petId === snapshot.pet.pet_id
      ? character
      : null;
  const [sitting, setSitting] = useState(false);
  const [petOpen, setPetOpen] = useState(false);
  // 正式宠物的场景形象只来自后端生效的角色版本；没有时退回原照肖像（临时），fixture 演示猫仍只在内部样例里出现。
  const figure = sprite ? null : worldCharacter;
  const [figureUnavailable, setFigureUnavailable] = useState(false);
  const figureFailed = useCallback(() => setFigureUnavailable(true), []);
  const figureLoaded = useCallback(() => setFigureUnavailable(false), []);
  // Fact position remains authoritative. Body visibility only permits preparing
  // a path; the current identity/resource/direction's loaded gait grants travel.
  const reduced = useReducedMotion();
  const bodyShown = sprite !== null || (!figureUnavailable && sceneAsset(figure, "neutral_full") !== null);
  const hidden = useSyncExternalStore(subscribeSceneVisibility, () => document.visibilityState === "hidden", () => true);
  const binding = sceneMotionBinding(snapshot.pet, figure, sprite || snapshot.data_origin === "fixture" ? null : sceneMotion);
  const [capability, setCapability] = useState<{ key: string; facing: string | null; value: HomeMotionCapability } | null>(null);
  const capCurrent = capability?.key === binding.resourceKey && capability.value.identityKey === binding.identityKey &&
    capability.value.petId === snapshot.pet.pet_id && capability.value.identityGeneration === binding.identityGeneration;
  const allowed = !!capCurrent && capability.value.allowPathMotion && capability.value.proof === "loaded_metadata";
  const disabled = !here || hidden || reduced || binding.muted || introPaused;
  const stage = usePetStage({ petId: snapshot.pet.pet_id, room, life: here ? life : null, reduced, wander: bodyShown,
    pathGate: { key: binding.resourceKey, enabled: binding.enabled && bodyShown, disabled, allowed, facing: capCurrent ? capability.facing as "front" | "back" | "left" | "right" | null : null } });
  const capabilityOwner = useRef({ key: binding.resourceKey, facing: stage.facing ?? null });
  capabilityOwner.current = { key: binding.resourceKey, facing: stage.facing ?? null };
  const mountedScene = useRef(true);
  useLayoutEffect(() => { mountedScene.current = true; return () => { mountedScene.current = false; }; }, []);
  const ownerKey = binding.resourceKey, ownerFacing = stage.facing ?? null;
  const acceptCapability = useCallback((value: HomeMotionCapability) => {
    if (!mountedScene.current || capabilityOwner.current.key !== ownerKey || capabilityOwner.current.facing !== ownerFacing ||
      value.identityKey !== binding.identityKey || value.petId !== snapshot.pet.pet_id || value.identityGeneration !== binding.identityGeneration) return;
    setCapability((old) => old?.key === ownerKey && old.facing === ownerFacing && JSON.stringify(old.value) === JSON.stringify(value) ? old : { key: ownerKey, facing: ownerFacing, value });
  }, [ownerKey, ownerFacing, binding.identityKey, binding.identityGeneration, snapshot.pet.pet_id]);
  const frameGait = allowed && capability?.facing === stage.facing && !disabled;
  const petShown = here && !stage.gone;
  // 点 TA：蹦一下、冒几颗心（纯画面反应，不改 TA 的任何状态；不叫“照顾”）。睡着时不蹦、不冒心（不吵醒 TA），只照常打开气泡
  const [reacting, setReacting] = useState(false);
  const reactTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (reactTimer.current) clearTimeout(reactTimer.current);
  }, []);
  const react = () => {
    if (reduced || kind === "sleeping") return;
    if (reactTimer.current) clearTimeout(reactTimer.current);
    setReacting(true);
    reactTimer.current = setTimeout(() => setReacting(false), REACT_MS);
  };
  // 姿态（scenePose）：睡着睡姿；点 TA 那一下被摸；吃饭吃饭；在走走动；这一套里没有那张就中性站姿
  const pose = scenePose(figure, { kind, detail: activity?.detail?.kind, walking: stage.walking, petted: reacting });
  // TA 的框（位置与大小按 TA 此刻站的那一点）：TA 和头顶那一句共用同一个框、同样的平移时长，那一句跟着 TA 走
  const box = petBox(room, stage.at);
  const tagCenter = boxPct(stage.at).left;
  const tagAlign = tagCenter < 36 ? "start" : tagCenter > 64 ? "end" : "center";
  const note = characterNote(figure);
  // 首套形象还在路上 / 没画成（只认首套，主人自己点的重画不算）：气泡里给一条回等待页的路，离开去星球看看也能回来（c84a D0812-1）
  const arrival = arrivalNotice(figure ?? null);
  // 睡姿真有才画睡着的样子（睡姿图、慢呼吸、飘 z）。
  // 没有睡姿、但 TA 真睡着了（life.activity.kind，和头顶状态牌同一个投影，不看作息该不该睡）：给“睡着了”的画面提示（dozing，UI2-HOME-SLEEP-CUE-01，
  // I 10-14：原来站着睁眼笑、只靠一行字说在睡觉，图文打架）——形象压暗一点、往下沉一点，头顶照样飘 z；不换图、不加字、不加呼吸。
  // 这只是提示，不算睡姿（c84a D2116-5「站着加 zZ 不算睡姿」照旧：缺的睡姿照旧记着缺）。说不清、醒着都没有。
  const sleepPose = !figureUnavailable && kind === "sleeping" && sceneAsset(figure, "sleeping") !== null;
  const dozing = kind === "sleeping" && !sleepPose;
  const object = snapshot.welcome?.details.find(
    (detail) => detail.kind === "favorite_object",
  );
  const unread = snapshot.unread.messages;
  const name = snapshot.pet.name;
  // 菜园是二级页（/garden）：院子里只留一个入口，露出最值得看的那块地；状态点开才说。
  const ripe = snapshot.plots.filter((p) => p.stage === "ripe");
  const growing = snapshot.plots.filter((p) => p.stage === "growing");
  const open = snapshot.plots.filter((p) => p.stage === "empty" || p.stage === "harvested");
  const gardenStatus = ripe.length ? `${ripe.length} 块熟了` : open.length ? "有空地可以种" : growing.length ? "都在长" : null;

  // 场景里此刻真有的、可以点的东西（按从上到下的顺序，也是第一次提示时依次轻闪的顺序）。
  const things: ThingKey[] = [
    inside ? "cabinet" : "door",
    ...(petShown ? (["pet"] as const) : []),
    ...(object ? (["memento"] as const) : []),
    ...(inside ? [] : (["garden"] as const)),
  ];
  // 遮挡片：TA 脚底在它落地那一行后面时画在 TA 上面（homeStage isBehind）；茶几盖住 TA 时，桌上的相框跟着抬到茶几上面
  // Geometry anchors use whole stage pixels; quantize only the depth ordering,
  // never substitute the segment destination for the visible foot.
  const depthFoot = { ...stage.depthAt, y: Math.round(stage.depthAt.y) };
  const occluders = plate.occluders.map((o) => ({ ...o, over: petShown && isBehind(room, depthFoot, o) }));
  const tableOver = occluders.some((o) => o.key === "coffee-table" && o.over);
  const glowOrder: string[] = [...things];
  if (away) glowOrder.splice(1, 0, "note");
  const hinting = useFirstVisitHint(snapshot.pet.pet_id, introPaused, glowOrder.length);
  const glow = (key: string): { className: string; style?: CSSProperties } => {
    const order = glowOrder.indexOf(key);
    if (order < 0) return { className: "" };
    return {
      className: hinting ? " is-hinting" : "",
      style: { "--hint-delay": `${HINT_START_MS + order * HINT_STEP_MS}ms` } as CSSProperties,
    };
  };

  const viewportRef = useRef<HTMLDivElement>(null);
  const camera = useHomeViewportFit(room, viewportRef);
  const sceneRef = useRef<HTMLElement>(null);
  const railRef = useRef<HTMLElement>(null);
  // 头顶那一句别盖住桌上的相框（2531 R28 8.1、I 10-03）：按 TA 要站的那一点（不是走到一半的位置）算这一句的框，
  // 会压到相框就挂到 TA 脚下（homeStage.tagCovers）。量的是真实字框与场景大小；量不出来（jsdom、还没排版）就照旧挂在头顶。
  // 桌上真有相框才算（相册空时 HomeAlbumFrame 什么都不画）：相框是场景的直接子元素、相册读回来才画，盯着场景的子元素变化。
  const tagRef = useRef<HTMLDivElement>(null);
  const [tagBelow, setTagBelow] = useState(false);
  const [framePresent, setFramePresent] = useState(false);
  const [, setSceneWidth] = useState(0);
  useEffect(() => {
    const el = sceneRef.current;
    if (!el) return;
    const check = () => setFramePresent(el.querySelector(":scope > [data-testid='home-table-photo']") !== null);
    check();
    const children = typeof MutationObserver === "undefined" ? null : new MutationObserver(check);
    children?.observe(el, { childList: true });
    const size = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => setSceneWidth(el.clientWidth));
    size?.observe(el);
    return () => {
      children?.disconnect();
      size?.disconnect();
    };
  }, []);
  useLayoutEffect(() => {
    const scene = sceneRef.current;
    const status = tagRef.current?.querySelector<HTMLElement>(".ps-living-status");
    const width = scene?.clientWidth ?? 0;
    const height = scene?.clientHeight ?? 0;
    let below = false;
    if (inside && framePresent && petShown && status && width > 0 && height > 0) {
      const sx = width / FRAME.width;
      const sy = height / (FRAME.bottom - FRAME.top);
      const bottom = (stage.at.y - bodyHeight(room, stage.at.y) - FRAME.top) * sy - 6;
      const tw = status.offsetWidth;
      const cx = stage.at.x * sx;
      const left = tagAlign === "start" ? cx - 32 : tagAlign === "end" ? cx + 32 - tw : cx - tw / 2;
      const frame = {
        left: (PHOTO_FRAME.x - PHOTO_FRAME.width / 2) * sx,
        right: (PHOTO_FRAME.x + PHOTO_FRAME.width / 2) * sx,
        top: (PHOTO_FRAME.y - PHOTO_FRAME.height - FRAME.top) * sy,
        bottom: (PHOTO_FRAME.y - FRAME.top) * sy,
      };
      below = tagCovers({ left, right: left + tw, top: bottom - status.offsetHeight, bottom }, frame);
    }
    setTagBelow(below);
  });
  const triggers = useRef(new Map<string, HTMLElement>());
  const courtyardExit = useRef<HTMLButtonElement>(null);
  const roomKeyIntent = useRef<{ trigger: HTMLButtonElement; petId: string; inside: boolean } | null>(null);
  const roomFocusIntent = useRef<{ trigger: HTMLButtonElement; petId: string; inside: boolean } | null>(null);
  // Only a door activated by a keyboard event transfers focus. A focused mouse click is not keyboard intent.
  const armRoomKey = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    roomKeyIntent.current = !event.repeat && (event.key === "Enter" || event.key === " ")
      ? { trigger: event.currentTarget, petId: snapshot.pet.pet_id, inside } : null;
  };
  const clearRoomKey = () => { roomKeyIntent.current = null; };
  useLayoutEffect(() => {
    const pending = roomFocusIntent.current;
    roomFocusIntent.current = null;
    roomKeyIntent.current = null;
    if (!pending || pending.petId !== snapshot.pet.pet_id || pending.inside !== inside) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== pending.trigger && active.isConnected) return;
    const target = inside ? courtyardExit.current : triggers.current.get("door");
    if (!target?.isConnected || target.matches(":disabled") || target.closest("[hidden], [inert]")) return;
    target.focus({ preventScroll: true });
  }, [inside, snapshot.pet.pet_id]);
  // 每件东西一个固定的 ref 回调：气泡靠它找到自己挨着的那件东西，点别处收起时也靠它判断“是不是点在它身上”。
  const refCallbacks = useRef(new Map<string, (el: HTMLElement | null) => void>());
  const register = useCallback((key: OpenKey) => {
    let callback = refCallbacks.current.get(key);
    if (!callback) {
      callback = (el: HTMLElement | null) => {
        if (el) triggers.current.set(key, el);
        else triggers.current.delete(key);
      };
      refCallbacks.current.set(key, callback);
    }
    return callback;
  }, []);
  const anchorOf = useCallback((key: string) => triggers.current.get(key) ?? null, []);
  const bubblePrefix = useId();
  const bubbleId = (key: OpenKey) => `${bubblePrefix}${key}`;
  const [openKey, setOpenKey] = useState<ThingKey | null>(null);
  useEffect(() => {
    if (kind !== "sleeping" && kind !== "away") return;
    if (reactTimer.current) clearTimeout(reactTimer.current);
    reactTimer.current = null;
    setReacting(false);
    if (kind === "away") { setPetOpen(false); setOpenKey(null); }
  }, [kind]);
  // 信箱在左侧小图标（两间都有）：新庭院底图的信箱烘在顶栏后面，点不到，不再在场景里另挂一个。
  // 左侧小图标写着名字、点一下就去，自己不冒气泡（只登记位置，给场景里的气泡让开）。
  const present: ThingKey[] = [...things];
  // 房间或 TA 的状态变了，原来那件东西不在了，气泡跟着消失。
  const openNow = openKey && present.includes(openKey) ? openKey : null;
  const toggle = (key: ThingKey) => setOpenKey((current) => (current === key ? null : key));

  useEffect(() => {
    if (!openNow) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      const trigger = triggers.current.get(openNow);
      const bubble = document.getElementById(`${bubblePrefix}${openNow}`);
      if (target && (trigger?.contains(target) || bubble?.contains(target))) return;
      setOpenKey(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpenKey(null);
      triggers.current.get(openNow)?.focus({ preventScroll: true });
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [openNow, bubblePrefix]);

  const changeRoom = (value: boolean, trigger?: HTMLButtonElement) => {
    roomFocusIntent.current = trigger && value !== inside
      ? { trigger, petId: snapshot.pet.pet_id, inside: value } : null;
    setOpenKey(null);
    const next = new URLSearchParams(params);
    if (value) next.set("room", "inside");
    // 有 life 时镜头默认对着 TA 那一间（多半是屋里）：点了庭院要写明，不然又回到默认；没有 life 照旧不写（默认就是庭院）。
    else if (life) next.set("room", "courtyard");
    else next.delete("room");
    // 换房间不加浏览器历史：在地图上按返回不会回到小窝的某个房间（刷新仍停在当前房间）。只换镜头，TA 在哪不变。
    setParams(next, { replace: true, state: preserveSceneState(location.state) });
  };
  const activateRoom = (value: boolean, event: ReactMouseEvent<HTMLButtonElement>) => {
    const intent = roomKeyIntent.current;
    clearRoomKey();
    const keyboard = event.detail === 0 && intent?.trigger === event.currentTarget
      && intent.petId === snapshot.pet.pet_id && intent.inside === inside;
    changeRoom(value, keyboard ? event.currentTarget : undefined);
  };
  const hit = (key: ThingKey, label: string, shown: string) => ({
    ref: register(key),
    type: "button" as const,
    "aria-label": label,
    "aria-expanded": openNow === key,
    "aria-controls": openNow === key ? bubbleId(key) : undefined,
    "data-name": shown,
    // 给互动效果层认是哪件东西（features/home/effects，I 做）：效果只是装饰，不改这里的点按行为
    "data-thing": key,
    onClick: () => toggle(key),
    style: glow(key).style,
  });
  const hitClass = (key: ThingKey, base: string) => `${base} ps-home-hit ps-home-glow${glow(key).className}`;
  const openPetSheet = () => {
    // 焦点先回到 TA 身上，面板收起时就会回到这里（气泡马上要消失）。
    triggers.current.get("pet")?.focus({ preventScroll: true });
    setOpenKey(null);
    setPetOpen(true);
  };

  // TA 此刻在哪、在做什么（只读 life 与 presence）：HOME-SCENE-REFLOW v2“状态放到宠物身边”——画面里有 TA 时写在 TA 头顶（见下面 ps-living-tag）；
  // TA 不在画面里（出门了、在另一间）时画面上已经说了（出门是门口便签，另一间是页签上的小图钉），这一句只留给读屏，不再是顶栏里一条胶囊
  const statusState = away ? "away" : kind === "sleeping" ? "asleep" : home ? "home" : "unknown";
  // TA 在另一间时，那一间页签的说明（aria-describedby）就是这一句
  const statusId = useId();
  const statusTag = (extra = "") => (
    <p id={statusId} className={`ps-living-status${extra ? ` ${extra}` : ""}`} data-testid="home-status" data-state={statusState}>
      <span className="ps-living-status__dot" aria-hidden="true" />
      {statusLine(name, life, away, home)}
    </p>
  );

  const bubbleFor = (key: ThingKey) => {
    const cameraKey = camera ? [camera.left, camera.width, camera.height, camera.stageWidth, camera.stageHeight].join(":") : "none";
    const common = { id: bubbleId(key), anchorKey: key, anchorOf, scene: sceneRef, viewport: viewportRef, rail: railRef, cameraKey };
    switch (key) {
      case "pet":
        return (
          <Bubble {...common} name={name} detail={doing ?? "在家"} note={note} place="bottom">
            {/* 原来底部横栏的“和 TA 说话”挪到点 TA 才出现的这里（HOME-SCENE-REFLOW v2：点宠物再展开操作）；睡着也能说，TA 醒了再回 */}
            <SourceLink className="ps-home-bubble__action is-main" to="/communicator">
              和 TA 说话
            </SourceLink>
            <button type="button" className="ps-home-bubble__action" onClick={openPetSheet}>
              陪 TA 待一会儿 <Icon name="chevron" size={14} />
            </button>
            {arrival ? (
              <Link className="ps-home-bubble__action" to={`/arriving?pet=${encodeURIComponent(snapshot.pet.pet_id)}`}>
                {arrival === "on_the_way" ? "看看形象进度" : "看看为什么"} <Icon name="chevron" size={14} />
              </Link>
            ) : null}
          </Bubble>
        );
      case "memento":
        return object ? (
          <Bubble {...common} name={object.text} detail="你为 TA 留下的生活叮嘱" place="right">
            <Link className="ps-home-bubble__action" to="/onboarding/reception?mode=supplement">
              补充叮嘱 <Icon name="chevron" size={14} />
            </Link>
          </Bubble>
        ) : null;

    }
  };
  const openThing = openNow;

  return (
    <>
    <div ref={viewportRef} className="ps-home-viewport" data-testid="home-viewport" style={{
      height: camera?.height,
      "--ps-camera-left": `${camera?.left ?? 0}px`,
      "--ps-camera-width": camera ? `${camera.width}px` : "100%",
      "--ps-camera-stage-height": camera ? `${camera.stageHeight}px` : "100%",
    } as CSSProperties}>
    <section
      ref={sceneRef}
      className={`ps-living-scene ${inside ? "is-inside" : "is-courtyard"}`}
      data-presence={snapshot.presence}
      data-pet-id={snapshot.pet.pet_id}
      data-character-set={figure?.active?.character_set_id ?? undefined}
      data-character-revision={figure?.active?.revision ?? undefined}
      data-audio="silent"
      data-hint={hinting ? "on" : undefined}
      data-sky={sky}
      // 画面上是哪一套图：night 夜图；day 白天图；fallback 夜里但夜图下不来、留在白天图（home.css 用原来的深藏青压暗）
      data-plate={nightPlate.night ? "night" : nightPlate.failed ? "fallback" : "day"}
      aria-label={inside ? "共同的家·屋内" : "共同的家·庭院"}
      // 桌上的相框：TA 在茶几后面时跟着茶几抬到 TA 上面（6）；平常也比 TA（1）高一层（2）——TA 站在茶几正前方时，
      // TA 的框（含透明边）会盖住相框，点相框点中 TA（2531 R28 8.1、I 10-03）。相框是能打开的入口，点按区让给它
      style={{ "--ps-tabletop-z": tableOver ? 6 : 2, width: camera?.stageWidth, height: camera?.stageHeight, left: camera?.left } as CSSProperties}
    >
      {/* 昼夜刚换（同一间）：旧底图垫在下面，新底图在上面淡入，淡入完就撤（useNightPlate） */}
      {fadeFrom ? <img className="ps-living-fade-from" src={fadeFrom.src} alt="" aria-hidden="true" data-testid="home-plate-fade-from" style={{ objectPosition: "50% 0%", height: `${plate.height * FRAME.width / plate.width / (FRAME.bottom - FRAME.top) * 100}%`, top: `${-plate.cropTop * FRAME.width / plate.width / (FRAME.bottom - FRAME.top) * 100}%` }} /> : null}
      <img
        className="ps-living-background"
        src={base}
        alt=""
        fetchPriority="high"
        data-motion={fadeFrom ? "enter" : undefined}
        // Intrinsic plate ratio, with cropTop removed in the same FRAME units as every foot/hotspot.
        style={{ objectPosition: "50% 0%", height: `${plate.height * FRAME.width / plate.width / (FRAME.bottom - FRAME.top) * 100}%`, top: `${-plate.cropTop * FRAME.width / plate.width / (FRAME.bottom - FRAME.top) * 100}%` }}
        onLoad={(event) => whenBasePainted(event.currentTarget, base, setBaseLoaded)}
      />
      {plate.motion && motionAllowed && baseLoaded === base ? <SceneMotion key={room} src={plate.motion} /> : null}
      {/* 互动效果层（I 做，./effects）：碰碰 TA、点柜子屋门这些可点的东西、底图里的花盆窗户小灯，晃一晃、冒点小东西。
          纯画面，不拦点按、不改任何状态。层级 0：在底图和环境微动（都是 -1）上面、天色（0，排在后面）下面，夜里一样被压暗；压在 TA 和可点物件下面 */}
      <HomeEffects scene={sceneRef} room={room} reduced={reduced} />
      {/* 天色：后端作息给的夜里、清晨、傍晚压一层光（白天不压）；屋里窗口、院子灯笼和门窗留一圈暖光。纯装饰 */}
      <div className="ps-living-sky" aria-hidden="true" data-testid="home-sky" style={{ height: `${(plate.height - plate.cropTop) * FRAME.width / plate.width / (FRAME.bottom - FRAME.top) * 100}%` }} />
      {inside
        ? BOWLS.map((p, i) => {
            const { left, top } = boxPct(p);
            return (
              <img
                key={i}
                className="ps-living-bowl"
                src={BOWL_SRC}
                alt=""
                data-testid="home-bowl"
                // 比 TA 靠前（离镜头近）的食盆盖在 TA 前面，靠后的在 TA 后面
                style={{ left: `${left.toFixed(2)}%`, top: `${top.toFixed(2)}%`, width: `${((BOWL_WIDTH / FRAME.width) * 100).toFixed(2)}%`, zIndex: petShown && p.y > stage.depthAt.y ? 2 : 0 }}
              />
            );
          })
        : null}
      {inside ? tablePhoto : null}
      <header className="ps-living-header">
        <h1 className="ps-home-title">{name}的小窝</h1>
        <Link
          to="/market"
          className="ps-living-wallet"
          aria-label={`${snapshot.wallet.balance} 星币，查看仓库与集市`}
        >
          <Icon name="coin" size={17} />
          <strong data-testid="home-wallet">{snapshot.wallet.balance}</strong>
        </Link>
        {petShown ? null : statusTag("visually-hidden")}
      </header>
      <SceneControls scene="home" sceneHref={mapFocusHref(snapshot.pet.pet_id)} unread={unread} railRef={railRef}
        petDisabled={!home && !away}
        onPet={() => { if (away) { void navigate(mapFocusHref(snapshot.pet.pet_id)); } else { if (!here) changeRoom(factRoom === "inside"); setPetOpen(true); } }}
        renderEntry={entry => <RailEntry railKey={entry.key} to={entry.to} short={entry.short} label={entry.label} icon={entry.icon} art={entry.art} artSet={entry.artSet} dot={entry.dot} register={register} />} />
      {inside ? (
        <>
          <Link ref={register("cabinet")} to="/collection" aria-label="收藏柜，看看收藏" data-name="收藏柜" data-thing="cabinet" className="ps-home-spot ps-home-spot--cabinet ps-home-hit ps-home-object" />
          <button ref={courtyardExit} type="button" className="ps-home-room-exit" data-testid="home-courtyard-exit" aria-label="走回庭院" onKeyDown={armRoomKey} onPointerDown={clearRoomKey} onBlur={clearRoomKey} onClick={(event) => activateRoom(false, event)}>
            <Icon name="back" size={17} /> 庭院
          </button>
        </>
      ) : (
        <button ref={register("door")} type="button" aria-label="屋门，进屋看看" data-name="进屋看看" data-thing="door" className="ps-home-spot ps-home-spot--door ps-home-hit ps-home-object" onKeyDown={armRoomKey} onPointerDown={clearRoomKey} onBlur={clearRoomKey} onClick={(event) => activateRoom(true, event)} />
      )}
      {petShown ? (
        <>
          <button
            {...hit("pet", `看看 ${name}${doing ? `，${doing}` : ""}${note ? `，${note}` : ""}`, name)}
            onClick={() => {
              toggle("pet");
              react();
            }}
            className={hitClass(
              "pet",
              `ps-living-pet ${sitting ? "is-sitting" : ""} ${sprite ? "is-photo" : !figureUnavailable && sceneAsset(figure, pose) ? "is-character" : "is-portrait"}${stage.walking ? " is-walking" : ""}${sleepPose ? " is-asleep" : ""}${dozing ? " is-dozing" : ""}${stage.waking ? " is-waking" : ""}${reacting ? " is-reacting" : ""}`,
            )}
            // 位置与大小按 TA 此刻站的那一点（远小近大），走动时按这一段的时长平移过去
            style={{ ...glow("pet").style, ...box, transitionDuration: `${stage.moveMs}ms` }}
            data-doing={life?.activity.detail?.kind ?? undefined}
            data-foot-x={stage.depthAt.x}
            data-foot-y={stage.depthAt.y}
            data-motion-evidence={stage.walking && frameGait ? "loaded-frame-gait" : "still"}
            data-frame-gait={frameGait ? "true" : undefined}
            data-gait-facing={stage.facing ?? undefined}
            data-gait-requested={stage.motionRequested ? "true" : undefined}
            data-testid="home-pet"
            data-pose={pose}
            // 效果层按它判睡没睡（没有睡姿图时 data-pose 不是 sleeping）：睡着被碰只起伏，不蹦、不冒心
            data-asleep={kind === "sleeping" ? "true" : undefined}
          >
            {/* 3D 猫演示挂载点（AVATAR-HOME-01，6796）：fixture 数据 + fixture 快照 + ?cat3d=1 才打开，关着时原样渲染下面的 2D；
                姿态按 TA 的状态给：样例的坐 / 趴；其余按 life.activity.kind——睡着是 asleep，醒着、刚到家、不知道（含没有 life）都是“醒着在家”，
                由 3D 层慢慢轮换环境小动作（不含睡、吃、喝、玩手机）。位置看 life.position：TA 只画在 TA 所在的那一间。 */}
            <Cat3DSlot dataOrigin={snapshot.data_origin} room={room} posture={sprite ? (sitting ? "sit" : "rest") : kind === "sleeping" ? "asleep" : "awake"}>
              {sprite ? (
                <>
                  <span className="ps-living-pet__shadow" aria-hidden="true" />
                  <img
                    src={sitting ? sprite.sit : sprite.rest}
                    alt={`${name}的内部测试形象`}
                  />
                </>
              ) : (
                // 刚到家：头上的状态牌已经写着“{名字}刚到家”，身下不再重复一句（6796 fx0800 实拍、I 10-03）
                sceneMotion ? <HomeMotionAdapter pet={snapshot.pet} state={figure} bodyRevision={binding.bodyRevision}
                  identityGeneration={binding.identityGeneration} motion={binding.motion} presence={here ? "home" : "away"}
                  mode={sceneMotionMode(life, stage.motionRequested === true)} desiredFacing={stage.facing ?? null}
                  scenePose={pose} reducedMotion={reduced} paused={hidden || introPaused} muted={binding.muted} onCapability={acceptCapability} /> :
                  <PetFigure pet={snapshot.pet} state={figure} pose={pose} said={null} onUnavailable={figureFailed} onAvailable={figureLoaded} />
              )}
            </Cat3DSlot>
            {/* 睡着：头顶慢慢飘起几个 z（减少动态效果时就静静挂着）。有睡姿时配睡姿图；没有睡姿时配压暗、下沉的形象（dozing） */}
            {sleepPose || dozing ? (
              <span className="ps-living-pet__zzz" aria-hidden="true" data-testid="home-pet-zzz">
                <i>z</i>
                <i>z</i>
                <i>Z</i>
              </span>
            ) : null}
            {reacting && kind !== "sleeping" ? (
              <span className="ps-living-pet__hearts" aria-hidden="true" data-testid="home-pet-hearts">
                <Icon name="heart" size={14} />
                <Icon name="heart" size={18} />
                <Icon name="heart" size={12} />
              </span>
            ) : null}
          </button>
          {/* TA 头顶那一句：和 TA 同一个框、同样的平移时长；TA 靠左右边时往里收，不压到场景边上和左侧小图标。不接收点按（点的是 TA） */}
          <div ref={tagRef} className="ps-living-tag" style={{ ...box, transitionDuration: `${stage.moveMs}ms` }} data-align={tagAlign} data-below={tagBelow ? "" : undefined} data-testid="home-status-tag">
            {statusTag()}
          </div>
          {sprite && !inside ? (
            <img
              className="ps-living-bag"
              src={sprite.bag}
              alt=""
              data-testid="home-bag"
            />
          ) : null}
        </>
      ) : null}
      {!inside ? (
        <img
          className="ps-living-cup"
          src={cup}
          alt=""
          data-testid="home-cup"
        />
      ) : null}
      {away ? (
        // TA 不在家（用户 10-03：只有出去玩的时候才放这一张）：窝是空的，门口留一张便签，写 TA 真实去了哪、大约多久（HomePage 按地图同一份状态算好）；
        // 读不到时照旧“我出门啦”。点它回到地图并对准 TA（?focus=）。透明空白纸面叠真实短标题，完整信息保留在可访问说明。
        <Link
          to={mapFocusHref(snapshot.pet.pet_id)}
          className="ps-living-note"
          title={`${awayNote?.title ?? "我出门啦"}${awayNote?.detail ? `\n${awayNote.detail}` : ""}`}
          aria-label={`${awayNote?.title ?? "我出门啦"}${awayNote?.detail ? `，${awayNote.detail.split("\n").join("，")}` : ""}，回到地图看看 ${name} 在哪`}
          data-testid="home-away-note"
          data-thing="away-note"
        >
          <CottageMaterial src="/ui-assets/UI045/v1/door-note-blank-v1.png" className="ps-cottage-note" width={1254} height={1254}>
            <span className="ps-cottage-note__title">{awayNote?.title ?? "我出门啦"} · 看看 TA</span>
          </CottageMaterial>
          <span className="visually-hidden">{awayNote?.detail ?? ""}</span>
        </Link>
      ) : null}
      {object ? (
        <button {...hit("memento", `你留下的叮嘱：${object.text}`, object.text)} className={hitClass("memento", "ps-home-memento")}>
          <Icon name="bookmark" size={16} />
        </button>
      ) : null}
      {!inside ? (
        <Link ref={register("garden")} to="/garden" aria-label={`去菜园${gardenStatus ? `，${gardenStatus}` : ""}`} className="ps-home-garden-path ps-home-hit" data-thing="garden" data-testid="home-garden-gate">
          <span className="ps-home-garden-path__sign" aria-hidden="true">
            <CottageMaterial src="/ui-assets/UI-GARDEN-WEBP-V2-20261013/garden-sign-blank-v1.webp" className="ps-cottage-garden" width={2020} height={778}>
              <span className="ps-cottage-garden__title">菜园</span>
            </CottageMaterial>
            {/*
              * 有菜熟了：木牌上放一颗刚摘下的菜（UI2-HOME-RIPE-01，I 10-13：菜熟了要在院子里看得出来，点进去能收）。
              * 用的是菜园里摘下那一颗的收成小图（UI-ASSET-003，表外作物退回成熟作物图），取第一块熟了的地；没有熟了的地就不放。
              * 收菜仍在菜园里：门照旧是一个直达 /garden 的链接（UI043 用户 10-07 第 22 条），小窝里不出收菜按钮。
              * 读屏名里本来就有「N 块熟了」，这张图只给看画面的人，不另起文字、不做卡片（UI045 用户嫌平卡）。
              */}
            {ripe.length ? <img className="ps-home-garden-path__ripe" src={produceVisual(ripe[0]!.crop_key)} alt="" data-motion="enter" data-testid="home-garden-ripe" /> : null}
          </span>
        </Link>
      ) : null}
      {/*
        * 遮挡片（r7k 从同一张底图里抠出的透明副本，贴回原位、同一个缩放裁切）：一直画着，TA 在它后面时抬到 TA 上面（is-over）。
        * 遮挡片自己就是底图的像素：盖在底图上看不出来；夜里、傍晚它里面再压一层和场景同一张天色（按整个场景的大小摆、用遮挡片本身的透明度裁），
        * 抬到天色上面也和底图一样暗。纯装饰，不接收点按。
        */}
      {!inside ? <>
          <CottageDecoration kind="flowerpot" src="/ui-assets/UI045/v1/courtyard-flowerpot.png" rect={{ x: 90, y: 950, width: 120, height: 123 }} width={1239} height={1270} />
          <CottageDecoration kind="crate" src="/ui-assets/UI045/v1/courtyard-crate-toolbasket.png" rect={{ x: 725, y: 1140, width: 115, height: 92 }} width={1402} height={1122} />
      </> : null}
      {occluders.map((o, i) => {
        const r = plateRectPct(room, o);
        const mask = `url("${o.src}")`;
        return (
          <div
            key={`${room}:${o.key}`}
            className={`ps-living-occluder${o.over ? " is-over" : ""}`}
            aria-hidden="true"
            data-testid="home-occluder"
            data-occluder={o.key}
            data-over={o.over ? "true" : "false"}
            style={{ left: `${r.left.toFixed(4)}%`, top: `${r.top.toFixed(4)}%`, width: `${r.width.toFixed(4)}%`, height: `${r.height.toFixed(4)}%`, WebkitMaskImage: mask, maskImage: mask }}
          >
            <img src={o.src} alt="" data-motion={fadeFrom ? "enter" : undefined} />
            {/* 昼夜刚换：旧遮挡片垫在下面（透明度和新的一样，同一个 mask 裁），新的在上面淡入 */}
            {fadeFrom ? <img className="ps-living-occluder__from" src={fadeFrom.occluders[i].src} alt="" /> : null}
            <span
              className="ps-living-sky ps-living-occluder__sky"
              style={{
                left: `${((-r.left / r.width) * 100).toFixed(4)}%`,
                top: `${((-r.top / r.height) * 100).toFixed(4)}%`,
                width: `${((100 / r.width) * 100).toFixed(4)}%`,
                height: `${((100 / r.height) * 100 * (plate.height - plate.cropTop) * FRAME.width / plate.width / (FRAME.bottom - FRAME.top)).toFixed(4)}%`,
              }}
            />
          </div>
        );
      })}
      {openThing ? bubbleFor(openThing) : null}
      {petOpen && here ? (
        <Sheet
          title={`陪 ${name} 待一会儿`}
          subtitle={sprite ? "内部角色动作示例，不改变宠物真实状态" : <StatusLine text={`TA ${panelActivity(activity, life?.rhythm?.sky ?? null) ?? "在家"}`} note={note} />}
          onClose={() => setPetOpen(false)}
        >
          <div className="ps-stack">
            {sprite ? (
              <Button
                variant="primary"
                onClick={() => {
                  setSitting(!sitting);
                  setPetOpen(false);
                }}
              >
                {sitting ? "让 TA 趴着休息" : "陪 TA 坐一会儿"}
              </Button>
            ) : null}
            <SourceLink className="ps-btn ps-btn--secondary" to="/communicator">
              给 TA 捎句话
            </SourceLink>
            <SourceLink className="ps-btn ps-btn--secondary" to="/play">
              和 TA 玩个小游戏
            </SourceLink>
            <Link className="ps-btn ps-btn--ghost" to="/journey">
              看看下一段旅途
            </Link>
            {sprite ? null : petActions}
          </div>
        </Sheet>
      ) : null}
    </section>
    {notice}
    </div>
    {/*
      * 原来这里是固定在底部的一整条操作栏（和 TA 说话 / 去菜园）。用户 10-03：“下面不应该横在那里”“不管怎么样都不要像表单”（HOME-SCENE-REFLOW v2）：
      * 撤掉。“和 TA 说话”在点 TA 冒出的气泡里；出门了看 TA 走到哪是窝边那张“我出门啦”；菜园在顶上的页签（熟了挂小点）和院子里的菜园门。
      */}
    </>
  );
}
